import { isUnincorporated, useClientKind } from './clientKind';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { useDirtyForm } from '@/kit/hooks/useDirtyForm';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { SectionTabs } from '@/kit/SectionTabs';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ReportHeadingRow } from '@/core/models/report-options.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportOptionsService } from '@/core/services/reportOptions';
import { SETTINGS_ROUTE, useReportClient } from './common';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';
import './CustomiseHeading.scss';

/** The field text for every heading, by key. */
type HeadingValues = Record<string, string>;

const valuesOf = (rows: ReportHeadingRow[]): HeadingValues =>
  Object.fromEntries(rows.map((row) => [row.key, row.displayName]));

/**
 * The amended name a heading's field text stands for: none (the default is used) when it is blank or the default
 * name itself, otherwise the trimmed text.
 */
const amendedNameOf = (row: ReportHeadingRow, text: string): string | null => {
  const trimmed = text.trim();
  return trimmed.length === 0 || trimmed === row.defaultName ? null : trimmed;
};

/**
 * Report Settings > Customise Headings: rename the standard Profit and Loss / Balance Sheet lines (the headings, their
 * order and default names come from the server). Design review 2 Nov 2026: the page is always editable; once a heading
 * differs from what was saved the header offers Save (stores the changed headings in one request) and Discard (puts back
 * what was last saved), and "Reset to default" fills every heading with its default name - saved only with Save, so
 * Discard still undoes it.
 */
/**
 * Balance sheet headings a sole trader's or partnership's accounts do not have (no share capital or company reserves,
 * and the owner's/partners' profit is not carried in a Profit and Loss Account reserve), so they are not offered for
 * renaming.
 */
const UNINCORPORATED_HIDDEN_HEADINGS =
  /^(capital and reserves|called up share capital|share premium account|revaluation reserves?|capital redemption reserve|reserves|special reserves|profit and loss account)$/i;

export function CustomiseHeading() {
  const navigate = useNavigate();
  const client = useReportClient();
  const isUnincorporatedClient = isUnincorporated(useClientKind(client?.id)?.clientType);
  /** Every heading as last loaded or saved - what Save compares with. */
  const [saved, setSaved] = useState<ReportHeadingRow[]>([]);
  /** The field text, and whether it differs from what was saved (Discard puts the saved text back). */
  const { values, setValues, dirty, discard, markSaved } = useDirtyForm<HeadingValues>({});
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const show = (rows: ReportHeadingRow[]) => {
    setSaved(rows);
    markSaved(valuesOf(rows));
  };

  useEffect(() => {
    if (!client) return;
    reportOptionsService.getHeadings(client.id).then(
      (rows) => alive.current && show(rows),
      (error) => alive.current && notifyError(error),
    );
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Discards every change since the last save, including a Reset to default not yet saved. */
  const discardChanges = () => {
    if (savingRef.current) return;
    discard();
  };

  /** Every heading back to its default name; nothing is stored until Save, so Discard undoes it. */
  const resetToDefault = () => {
    if (savingRef.current) return;
    setValues(Object.fromEntries(saved.map((row) => [row.key, row.defaultName])));
  };

  /**
   * Sends only the headings whose name changed since they were loaded or saved, so a heading left alone keeps its
   * saved name exactly. A failure keeps every change in the fields, still unsaved.
   */
  const save = async () => {
    if (!client || savingRef.current) return;
    const changes = saved
      .filter((row) => amendedNameOf(row, values[row.key] ?? '') !== (row.amendedName ?? null))
      .map((row) => ({ key: row.key, amendedName: amendedNameOf(row, values[row.key] ?? '') }));
    if (changes.length === 0) {
      // Nothing changed: no request, just show the saved names (e.g. untrimmed text tidied).
      markSaved(valuesOf(saved));
      return;
    }

    savingRef.current = true;
    setIsSaving(true);
    try {
      const rows = await reportOptionsService.saveHeadings({ clientId: client.id, headings: changes });
      if (!alive.current) return;
      show(rows);
      notifySuccess(NotificationTextMessage.successMessage);
    } catch (error) {
      notifyError(error);
    } finally {
      savingRef.current = false;
      if (alive.current) setIsSaving(false);
    }
  };

  const line = (row: ReportHeadingRow) => (
    <div className="body-detl" key={row.key}>
      <p className={row.indentLevel === 1 ? 'pl-1' : row.indentLevel === 2 ? 'pl-2' : undefined}>{row.defaultName}</p>
      <div>
        <InputField
          fieldClassName="w-260"
          // BUG-033: the old application's Customise Headings allowed 145 characters per heading.
          maxLength={145}
          name={row.key}
          aria-label={row.defaultName}
          value={values[row.key] ?? ''}
          onChange={(event) => {
            const text = event.target.value;
            setValues((current) => ({ ...current, [row.key]: text }));
          }}
        />
      </div>
    </div>
  );

  const section = (rows: ReportHeadingRow[]) => (
    <div className="ch-card">
      <div className="ch-card-body">
        <div className="head-title ch-head-title">
          <p>Default Name</p>
          <p>Amended Name</p>
        </div>
        <div className="ch-rows">{rows.map(line)}</div>
      </div>
    </div>
  );

  const profitAndLoss = saved.filter((row) => row.statementArea === 0);
  const balanceSheet = saved.filter(
    (row) =>
      row.statementArea === 1 && !(isUnincorporatedClient && UNINCORPORATED_HIDDEN_HEADINGS.test(row.defaultName.trim())),
  );

  return (
    <StickyReportSettingsFrame
      title="Customise Headings"
      hostClassName="customise-headings-host"
      onClose={() => navigate(SETTINGS_ROUTE)}
      // Always editable: Unsaved changes, Discard and Save appear once a heading differs from what was saved; Reset to default stays.
      dirty={dirty}
      actionsPlacement="footer"
      busy={isSaving}
      onSave={() => void save()}
      onDiscard={discardChanges}
      actions={
        saved.length > 0 && (
          <Button className="link hyperlink" disabled={isSaving} onClick={resetToDefault}>
            Reset to default
          </Button>
        )
      }
    >
      <LeaveGuard when={dirty} />
      <div className="sidenav-body pt-1">
        {/* Design review 2 Nov 2026: the two statements were side by side; one tab each instead. */}
        <SectionTabs
          ariaLabel="Statements"
          tabs={[
            { id: 'pl', label: 'Profit and Loss', infoText: 'Override the default wording for profit and loss headings.' },
            { id: 'bs', label: 'Balance Sheet', infoText: 'Override the default wording for balance sheet headings.' },
          ]}
        >
          {(active) => section(active === 'pl' ? profitAndLoss : balanceSheet)}
        </SectionTabs>
      </div>
    </StickyReportSettingsFrame>
  );
}
